import { useEffect, useState } from 'react';
import type { AuditEvent, FormEngine } from '@inside-us-aos/form-engine';

import styles from './AuditPanel.module.css';

/**
 * Phase 2 batch 3 Slice 6 — live audit-trail viewer.
 *
 * Subscribes to the form engine's audit emitter and renders each
 * `AuditEvent` as a structured row inside a collapsible <details>
 * panel. PII redaction is the engine's job (ADR 0013); this panel
 * just renders whatever the emitter provides — when a value is
 * marked `{ type: 'redacted', reason: 'pii' }`, we display the
 * sentinel string rather than any plaintext.
 *
 * Capped at 100 events to keep the DOM cheap; the engine itself
 * keeps no event history. Phase 5's persistence layer will buffer a
 * full append-only audit trail for compliance.
 */

const AUDIT_BUFFER_CAP = 100;

export interface AuditPanelProps {
  engine: FormEngine;
}

interface DisplayEvent extends AuditEvent {
  /** Stable index within the panel's buffer for React keys. */
  _seq: number;
}

function formatPayload(value: AuditEvent['oldValue']): string {
  if (value === undefined) return '∅';
  if (value.type === 'redacted') return '[redacted]';
  if (value.type === 'text') return JSON.stringify(value.value);
  if (value.type === 'select') return JSON.stringify(value.value);
  if (value.type === 'multi_select') {
    return `[${value.value.map(v => JSON.stringify(v)).join(', ')}]`;
  }
  if (value.type === 'date') return value.value;
  if (value.type === 'number') return String(value.value);
  if (value.type === 'boolean') return String(value.value);
  if (value.type === 'document_upload') {
    return `${value.value.uploadedFileIds.length} file(s)`;
  }
  return value.type;
}

export function AuditPanel({ engine }: AuditPanelProps): JSX.Element {
  const [events, setEvents] = useState<readonly DisplayEvent[]>([]);

  useEffect(() => {
    let seq = 0;
    return engine.subscribeAudit(event => {
      setEvents(prev => {
        const next: DisplayEvent[] = [
          ...prev,
          { ...event, _seq: ++seq },
        ];
        // Cap to the last AUDIT_BUFFER_CAP events.
        if (next.length > AUDIT_BUFFER_CAP) {
          return next.slice(next.length - AUDIT_BUFFER_CAP);
        }
        return next;
      });
    });
  }, [engine]);

  return (
    <details className={styles.panel}>
      <summary className={styles.panel__summary}>
        Audit trail ({events.length}{' '}
        {events.length === 1 ? 'event' : 'events'})
      </summary>
      {events.length === 0 ? (
        <p className={styles.panel__empty}>
          No mutations yet. Type into a field above and the panel will
          populate.
        </p>
      ) : (
        <ol className={styles.panel__list}>
          {events.map(event => (
            <li key={event._seq} className={styles.panel__row}>
              <time
                className={styles.panel__time}
                dateTime={new Date(event.timestamp).toISOString()}
              >
                {new Date(event.timestamp).toISOString().slice(11, 23)}
              </time>{' '}
              <code className={styles.panel__field}>{event.fieldId}</code>
              {event.instanceKey ? (
                <span className={styles.panel__instance}>
                  [{event.instanceKey}]
                </span>
              ) : null}{' '}
              <span className={styles.panel__delta}>
                {formatPayload(event.oldValue)} →{' '}
                {formatPayload(event.newValue)}
              </span>
            </li>
          ))}
        </ol>
      )}
    </details>
  );
}
